/**
 * Order mutations for the Size & Paper flow. Every path that creates or edits
 * a print runs Print Check synchronously (README: "runs automatically on
 * every photo×size the moment it's added") and keeps unitPrice / effectiveDpi
 * in sync. Used by the apply bar, grid cards, Print Check banner, review
 * tray, crop editor and the mobile size picker.
 *
 * Decisions encoded here (see also lib/sizePaper):
 *  - "Add prints" APPENDS: a photo can hold many size+paper combos. Adding an
 *    identical combo again merges into that print's qty (chips stay unique);
 *    it never clobbers other sizes.
 *  - Choosing "Crop" (bulk or per-row) bakes the centered auto-crop rect into
 *    cropRect — an explicit decision the tray can distinguish from the silent
 *    default, and an identical bake output.
 *  - "Print anyway" sets overrideLowRes; the print re-enters totals but keeps
 *    printCheck 'lowres' on the wire (recorded for the lab).
 */

import {
  buildCatalog,
  ctypeSizeId,
  fineArtSizeId,
  framedProduct,
  parseSizeId,
  seedPricing,
  type BorderMode,
  type PaperId,
  type ResolvedSize,
} from '@rpl/pricing';
import { useMemo } from 'react';
import { customResolvedSize, isCustomSizeId } from '../../lib/customSize';
import {
  centeredAutoCropRect,
  checkPrint,
  DEFAULT_THRESHOLDS,
  ratioFit,
  thresholdsFromPricing,
  type PrintCheckThresholds,
} from '../../lib/printCheck';
import { rotatedDims, type Rotation } from '../../lib/rotate';
import {
  buildBulkPrints,
  buildPrintFields,
  effectiveBorderFor,
  findLooseMergeTarget,
  isBlockedPrint,
  isCtPaper,
  isPaperValidForSize,
  isFineArtBorderUndecided,
  largestFittingSize,
  paperForSize,
  priceForSafe,
  type WizardScope,
} from '../../lib/sizePaper';
import type {
  BwStyle, CropRect, FitMode, LocalPhoto, UiPrint } from '../../lib/types';
import { uid } from '../../lib/util';
import { useStore } from '../../store/store';

export interface OrderActions {
  catalog: Map<string, ResolvedSize>;
  thresholds: PrintCheckThresholds;
  /**
   * Append size+paper+qty to every SELECTED (ready) photo. `border` stamps a
   * chosen border on freshly-created prints (undefined = default 'none', which
   * leaves a fine-art print undecided for the Continue gate). Returns the ids of
   * the prints touched (created or merged) so callers can react.
   */
  addToSelection: (sizeId: string, paper: PaperId, qty: number, border?: BorderMode | null) => string[];
  /**
   * Add a FRAMED print (frameId set, coupled framed price, border 'none') to every
   * SELECTED (ready) photo — the Configure-pane "Add as Framed Print" path. Merges
   * into an existing matching framed line. Returns the touched print ids.
   */
  addFramedToSelection: (sizeId: string, paper: PaperId, qty: number, frameId: string) => string[];
  /**
   * Wizard bulk-apply (round 6, scoped):
   *  - 'printless': APPENDS the one config to every ready photo that has NO
   *    prints yet — existing prints are untouched (the default when some
   *    photos are already set up, and the fresh-order path).
   *  - 'all': REPLACE-ALL — clears every existing print (loose or framed) and
   *    applies the config to every ready photo (the explicit re-run path; the
   *    summary asks "Replace everything with this new setup?").
   */
  bulkApplyAll: (
    sizeId: string,
    paper: PaperId,
    qty: number,
    border: BorderMode | null,
    scope: WizardScope,
  ) => string[];
  /** Find-or-create ONE print for a photo×size×paper; returns its id (for crop). */
  ensurePrint: (
    photoId: string,
    sizeId: string,
    paper: PaperId,
    border?: BorderMode | null,
  ) => string | null;
  /** Set one print's border (marks fine-art prints decided). */
  setBorder: (printId: string, mode: BorderMode) => void;
  /** Apply a border across loose prints (selected photos, or all). */
  bulkSetBorder: (mode: BorderMode, scope: 'selected' | 'all') => void;
  /** Resolve the fine-art Continue gate: set every undecided fine-art print. */
  decideFineArtBorders: (mode: BorderMode) => void;
  /** Set/adjust qty for one photo×size×paper (mobile steppers). 0 removes. */
  setQty: (photoId: string, sizeId: string, paper: PaperId, qty: number) => void;
  /** Per-print fit decision (resolves a ratio flag with white borders). */
  setFit: (printId: string) => void;
  /** Per-print crop decision — bakes the centered auto-crop rect (resolved). */
  setCrop: (printId: string) => void;
  /**
   * Persist a specific crop rect on one print — the Review-card drag-to-crop
   * save (round 8). Becomes a custom crop; re-runs Print Check like setCrop.
   */
  setCustomCrop: (printId: string, rect: CropRect) => void;
  /** Bulk: apply crop/fit to every ratio-mismatch print. */
  bulkRatio: (mode: FitMode) => void;
  /** Swap a blocked print to the largest size that stays ≥ acceptable DPI. */
  swapToMax: (printId: string) => void;
  /** Remove one print (the "Remove size" fix). */
  removeSize: (printId: string) => void;
  /** Low-res override — include the print anyway (recorded). */
  printAnyway: (printId: string) => void;
  /**
   * Crop-editor save — the full print edit: size / paper / fit mode / crop rect,
   * plus optional qty, an explicit border choice and a frame decision (round 5:
   * the editor carries the same stocked-frames dropdown as Configure).
   * `frameId` semantics: undefined = keep as-is, null = unframe, id = frame.
   * Merges into an identical sibling (loose: size+paper+border; framed:
   * size+paper+frame).
   */
  saveCropEdit: (
    printId: string,
    edit: {
      sizeId: string;
      fitMode: FitMode;
      cropRect: CropRect | null;
      qty?: number;
      paper?: PaperId;
      borderMode?: BorderMode;
      /** Custom white-border width mm; undefined = keep, null = default. */
      borderMm?: number | null;
      frameId?: string | null;
      /** Image rotation (crop rect is defined on the rotated image). */
      rotation?: Rotation;
      /** Black & white conversion for this print. */
      bw?: boolean;
      /** B&W style; only meaningful when bw. */
      bwStyle?: BwStyle | null;
      /** v2/v3 strength 0..100; only meaningful with a v2/v3 style. */
      bwStrength?: number | null;
    },
  ) => void;
  /**
   * "Add a print" from the per-image print editor (round-4 tile ✎ / lightbox
   * "Order print"): create ONE print for the photo with the editor's full
   * config (size / paper / qty / border / frame / crop). Merges into an
   * identical sibling (geometry updates, quantities sum). Returns the touched
   * print id, or null when the photo/size/frame is unknown.
   */
  addPrintFromEditor: (
    photoId: string,
    edit: {
      sizeId: string;
      fitMode: FitMode;
      cropRect: CropRect | null;
      qty: number;
      paper: PaperId;
      borderMode?: BorderMode;
      borderMm?: number | null;
      frameId?: string | null;
      rotation?: Rotation;
      bw?: boolean;
      bwStyle?: BwStyle | null;
      bwStrength?: number | null;
    },
  ) => string | null;
  /** The swap target for a blocked print (drives "Use {size} max" labels). */
  swapTargetFor: (print: UiPrint) => ResolvedSize | null;
}

export function useOrderActions(): OrderActions {
  const pricing = useStore((s) => s.pricing) ?? seedPricing;
  const customSizes = useStore((s) => s.customSizes);

  return useMemo<OrderActions>(() => {
    const catalog = buildCatalog(pricing);
    // Session custom sizes join the catalog so every size-resolving path
    // (editor, previews, borders, buildPrintFields) handles cu- ids natively.
    if (pricing.customSizing?.enabled) {
      for (const id of customSizes) {
        if (!catalog.has(id) && isCustomSizeId(id)) catalog.set(id, customResolvedSize(id));
      }
    }
    const thresholds = pricing.printCheck
      ? thresholdsFromPricing(pricing.printCheck)
      : DEFAULT_THRESHOLDS;

    const state = () => useStore.getState();

    function photoById(photoId: string): LocalPhoto | undefined {
      return state().photos.find((p) => p.id === photoId);
    }

    /** Build a fresh print for photo×size×paper with Print Check applied. */
    function buildPrint(
      photo: LocalPhoto,
      sizeId: string,
      paper: PaperId,
      qty: number,
      border?: BorderMode | null,
    ): UiPrint | null {
      const entry = catalog.get(sizeId);
      if (!entry) return null;
      return { id: uid('pr'), ...buildPrintFields(photo, sizeId, paper, qty, border, entry, pricing, thresholds) };
    }

    function addToSelection(
      sizeId: string,
      rawPaper: PaperId,
      qty: number,
      border?: BorderMode | null,
    ): string[] {
      // Safety: never let an incompatible size+paper combo into prints[] — a
      // stale hydrated size could otherwise crash computeTotals app-wide.
      const paper = paperForSize(pricing, sizeId, rawPaper);
      const { photos, prints, selection, addPrints, updatePrint } = state();
      const fresh: UiPrint[] = [];
      const touched: string[] = [];
      for (const photo of photos) {
        if (!selection.has(photo.id) || photo.status !== 'ready') continue;
        // Border is part of the merge key — a white-border 4×6 and an edge 4×6
        // on the same photo are separate prints (round-3 decision).
        const existing = findLooseMergeTarget(prints, photo.id, sizeId, paper, border);
        if (existing) {
          updatePrint(existing.id, { qty: existing.qty + qty });
          touched.push(existing.id);
        } else {
          const print = buildPrint(photo, sizeId, paper, qty, border);
          if (print) {
            fresh.push(print);
            touched.push(print.id);
          }
        }
      }
      if (fresh.length) addPrints(fresh);
      return touched;
    }

    function addFramedToSelection(
      sizeId: string,
      rawPaper: PaperId,
      qty: number,
      frameId: string,
    ): string[] {
      const paper = paperForSize(pricing, sizeId, rawPaper);
      const entry = catalog.get(sizeId);
      if (!entry) return [];
      let price: number | null;
      try {
        price = framedProduct(pricing, frameId).price;
      } catch {
        return [];
      }
      const { photos, prints, selection, addPrints, updatePrint } = state();
      const fresh: UiPrint[] = [];
      const touched: string[] = [];
      for (const photo of photos) {
        if (!selection.has(photo.id) || photo.status !== 'ready') continue;
        const existing = prints.find(
          (p) =>
            p.photoId === photo.id &&
            p.frameId === frameId &&
            p.sizeId === sizeId &&
            p.paper === paper &&
            !(p.bw ?? false),
        );
        if (existing) {
          updatePrint(existing.id, { qty: existing.qty + qty });
          touched.push(existing.id);
        } else {
          const fields = buildPrintFields(photo, sizeId, paper, qty, 'none', entry, pricing, thresholds);
          const print: UiPrint = {
            id: uid('pr'),
            ...fields,
            unitPrice: price,
            frameId,
            borderMode: 'none',
            borderDecided: true,
          };
          fresh.push(print);
          touched.push(print.id);
        }
      }
      if (fresh.length) addPrints(fresh);
      return touched;
    }

    function bulkApplyAll(
      sizeId: string,
      rawPaper: PaperId,
      qty: number,
      border: BorderMode | null,
      scope: WizardScope,
    ): string[] {
      const paper = paperForSize(pricing, sizeId, rawPaper);
      const { photos, prints, replacePrints, addPrints } = state();

      if (scope === 'printless') {
        // Round 6: the wizard only configures photos that have NO prints yet —
        // photos the customer already set up are left exactly as they are.
        const withPrints = new Set(prints.map((p) => p.photoId));
        const targets = photos.filter((p) => !withPrints.has(p.id));
        const fresh: UiPrint[] = buildBulkPrints(
          targets, sizeId, paper, qty, border, catalog, pricing, thresholds,
        ).map((f) => ({ id: uid('pr'), ...f }));
        if (fresh.length === 0) return [];
        addPrints(fresh);
        return fresh.map((p) => p.id);
      }

      // REPLACE-ALL (explicit re-run): every existing print (loose or framed,
      // on every photo) is dropped and each ready photo gets the one config.
      const fresh: UiPrint[] = buildBulkPrints(
        photos, sizeId, paper, qty, border, catalog, pricing, thresholds,
      ).map((f) => ({ id: uid('pr'), ...f }));
      if (fresh.length === 0) return [];
      replacePrints(fresh);
      return fresh.map((p) => p.id);
    }

    function ensurePrint(
      photoId: string,
      sizeId: string,
      rawPaper: PaperId,
      border?: BorderMode | null,
    ): string | null {
      const paper = paperForSize(pricing, sizeId, rawPaper);
      const { prints, addPrints } = state();
      // Border joins the identity: crop-editing a WB config never reuses an
      // edge print (and vice versa).
      const existing = findLooseMergeTarget(prints, photoId, sizeId, paper, border);
      if (existing) return existing.id;
      const photo = photoById(photoId);
      if (!photo) return null;
      const print = buildPrint(photo, sizeId, paper, 1, border);
      if (!print) return null;
      addPrints([print]);
      return print.id;
    }

    function setBorder(printId: string, mode: BorderMode): void {
      state().setBorderMode(printId, mode);
    }

    function bulkSetBorder(mode: BorderMode, scope: 'selected' | 'all'): void {
      state().bulkSetBorder(mode, scope);
    }

    function decideFineArtBorders(mode: BorderMode): void {
      const { prints, setBorderMode } = state();
      for (const p of prints) {
        if (!isBlockedPrint(p) && isFineArtBorderUndecided(p)) {
          setBorderMode(p.id, effectiveBorderFor(p.sizeId, mode));
        }
      }
    }

    function setQty(photoId: string, sizeId: string, paper: PaperId, qty: number): void {
      const { prints, addPrints, updatePrint, removePrint } = state();
      // Border is part of the print identity — this legacy per-combo setter
      // addresses the print-to-edge variant.
      const existing = findLooseMergeTarget(prints, photoId, sizeId, paper, 'none');
      if (existing) {
        if (qty <= 0) removePrint(existing.id);
        else updatePrint(existing.id, { qty });
        return;
      }
      if (qty <= 0) return;
      const photo = photoById(photoId);
      if (!photo) return;
      const print = buildPrint(photo, sizeId, paper, qty);
      if (print) addPrints([print]);
    }

    /** Re-run Print Check for a print after a geometry change and patch it. */
    function recheck(print: UiPrint, patch: Partial<UiPrint>): Partial<UiPrint> {
      const merged = { ...print, ...patch };
      const entry = catalog.get(merged.sizeId);
      const photo = photoById(merged.photoId);
      if (!entry || !photo) return patch;
      // Rotation is applied BEFORE crop: crop_rect and DPI run on rotated dims.
      const [rw, rh] = rotatedDims(photo.width, photo.height, merged.rotation ?? 0);
      const result = checkPrint(
        { width: rw, height: rh },
        entry.inches,
        merged.fitMode === 'crop' ? merged.cropRect : null,
        merged.fitMode,
        entry.family,
        thresholds,
      );
      return {
        ...patch,
        printCheck: result.status,
        effectiveDpi: result.dpi,
        // a geometry change invalidates a previous "print anyway" only when
        // the result is no longer low-res; keep the override otherwise.
        overrideLowRes: result.status === 'lowres' ? merged.overrideLowRes : false,
      };
    }

    function setFit(printId: string): void {
      const { prints, updatePrint } = state();
      const print = prints.find((p) => p.id === printId);
      if (!print) return;
      updatePrint(printId, recheck(print, { fitMode: 'fit', cropRect: null }));
    }

    function setCrop(printId: string): void {
      const { prints, updatePrint } = state();
      const print = prints.find((p) => p.id === printId);
      if (!print) return;
      const photo = photoById(print.photoId);
      const entry = catalog.get(print.sizeId);
      if (!photo || !entry) return;
      const [rw, rh] = rotatedDims(photo.width, photo.height, print.rotation ?? 0);
      const rect = centeredAutoCropRect(rw, rh, entry.inches);
      updatePrint(printId, recheck(print, { fitMode: 'crop', cropRect: rect }));
    }

    function setCustomCrop(printId: string, rect: CropRect): void {
      const { prints, updatePrint } = state();
      const print = prints.find((p) => p.id === printId);
      if (!print) return;
      updatePrint(printId, recheck(print, { fitMode: 'crop', cropRect: rect }));
    }

    function bulkRatio(mode: FitMode): void {
      const { prints } = state();
      for (const p of prints) {
        const photo = photoById(p.photoId);
        const entry = catalog.get(p.sizeId);
        if (!photo || !entry) continue;
        // Geometry-based (NOT current printCheck): a fitted print reports 'ok',
        // so keying off status made "Fit all" un-undoable. Any print whose photo
        // ratio mismatches its size is eligible → the toggle now works both ways.
        if (ratioFit(photo.width, photo.height, entry.inches, thresholds.ratioTolerance) !== 'autocrop')
          continue;
        if (mode === 'fit') setFit(p.id);
        else setCrop(p.id);
      }
    }

    function swapTargetFor(print: UiPrint): ResolvedSize | null {
      const photo = photoById(print.photoId);
      if (!photo) return null;
      const sameFamily = largestFittingSize(
        pricing,
        catalog,
        photo,
        print.sizeId,
        print.paper,
        thresholds,
      );
      if (sameFamily) return sameFamily;
      // Owner 2026-07-27: a low-res FINE-ART print often has no fine-art size
      // above the 180 DPI floor, so the card offered no recommendation at all.
      // Fall back ACROSS families (C-Type's floor is 100) so there is always a
      // sharp option to name; retarget's paperForSize moves the paper with it.
      // Framed prints are excluded — their size is pinned to the frame.
      if (print.frameId) return null;
      let best: ResolvedSize | null = null;
      let bestArea = -1;
      for (const entry of catalog.values()) {
        if (entry.sizeId === print.sizeId) continue;
        if (isCustomSizeId(entry.sizeId)) continue;
        const paper = paperForSize(pricing, entry.sizeId, print.paper);
        if (!isPaperValidForSize(pricing, entry.sizeId, paper)) continue;
        const [rw, rh] = rotatedDims(photo.width, photo.height, print.rotation ?? 0);
        const result = checkPrint({ width: rw, height: rh }, entry.inches, null, 'crop', entry.family, thresholds);
        if (result.status === 'lowres') continue;
        const area = entry.inches[0] * entry.inches[1];
        if (area > bestArea) {
          best = entry;
          bestArea = area;
        }
      }
      return best;
    }

    /**
     * Re-target a print (swap fix / crop-editor save): new size, and optionally
     * a new paper, qty, an explicit border choice and a FRAME decision from the
     * editor. `opts.frameId` semantics: undefined = keep the print's current
     * frame state; null = unframe; a frame id = (re)frame at the coupled framed
     * price. If the photo already holds an identical combo (loose:
     * size+paper+BORDER; framed: size+paper+FRAME), quantities merge into that
     * print instead of duplicating the chip.
     */
    function retarget(
      print: UiPrint,
      sizeId: string,
      geometry: { fitMode: FitMode; cropRect: CropRect | null },
      opts?: {
        qty?: number;
        paper?: PaperId;
        borderMode?: BorderMode;
        borderMm?: number | null;
        frameId?: string | null;
        rotation?: Rotation;
        bw?: boolean;
        bwStyle?: BwStyle | null;
        bwStrength?: number | null;
      },
    ): void {
      const { prints, updatePrint, removePrint } = state();
      const nextFrameId = opts?.frameId === undefined ? print.frameId ?? null : opts.frameId;
      const framed = !!nextFrameId;
      let framedPrice: number | null = null;
      if (framed) {
        try {
          framedPrice = framedProduct(pricing, nextFrameId!).price;
        } catch {
          return; // unknown frame — leave the order untouched
        }
      }
      const requestedPaper = opts?.paper ?? print.paper;
      const paper = paperForSize(pricing, sizeId, requestedPaper);
      const qty = opts?.qty ?? print.qty;
      const nextRotation: Rotation = opts?.rotation ?? print.rotation ?? 0;
      const nextBw = opts?.bw ?? print.bw ?? false;
      // Style follows B&W: null whenever colour; an explicit editor choice
      // wins over the print's own (mirrors border_mm).
      const nextBwStyle: BwStyle | null = !nextBw
        ? null
        : opts?.bwStyle !== undefined
          ? opts.bwStyle
          : print.bwStyle ?? null;
      // Strength only exists on v2/v3; an explicit editor value wins.
      const nextBwStrength: number | null =
        nextBwStyle === 'v2' || nextBwStyle === 'v3'
          ? opts?.bwStrength !== undefined
            ? opts.bwStrength
            : print.bwStrength ?? null
          : null;
      // An explicit editor choice wins; otherwise carry the print's border
      // across the size change, clamped to what the new size offers.
      const requestedBorder: BorderMode = framed
        ? 'none'
        : opts?.borderMode ?? print.borderMode ?? 'none';
      const nextBorder = effectiveBorderFor(sizeId, requestedBorder);
      // Custom width follows the border: cleared whenever the effective mode
      // is not white; an explicit editor value wins over the print's own.
      const nextBorderMm: number | null =
        nextBorder !== 'white'
          ? null
          : opts?.borderMm !== undefined
            ? opts.borderMm
            : print.borderMm ?? null;
      const dup = framed
        ? prints.find(
            (p) =>
              p.id !== print.id &&
              p.frameId === nextFrameId &&
              p.photoId === print.photoId &&
              p.sizeId === sizeId &&
              p.paper === paper &&
              (p.bw ?? false) === nextBw,
          )
        : findLooseMergeTarget(prints, print.photoId, sizeId, paper, nextBorder, nextBw, print.id);
      // A differing custom border width is a DIFFERENT print — never merge
      // a 12mm-border line into a default-border one.
      const dupSafe = dup
        && (dup.borderMm ?? null) === nextBorderMm
        && (dup.bwStyle ?? null) === nextBwStyle
        && (dup.bwStrength ?? null) === nextBwStrength
          ? dup
          : undefined;
      const target = dupSafe ?? print;
      // Re-gate a print that newly becomes fine art (unless the editor chose).
      const newFamily = parseSizeId(sizeId).family;
      const oldFamily = parseSizeId(target.sizeId).family;
      const nextDecided =
        framed || newFamily === 'ctype' || opts?.borderMode != null
          ? true
          : oldFamily === 'fine_art'
            ? target.borderDecided ?? false
            : false;
      const patch = recheck(
        { ...target, sizeId, paper },
        {
          sizeId,
          paper,
          fitMode: geometry.fitMode,
          cropRect: geometry.fitMode === 'crop' ? geometry.cropRect : null,
          // Framed prints take the coupled framed price; loose prints reprice.
          unitPrice: framed ? framedPrice : priceForSafe(pricing, sizeId, paper),
          frameId: nextFrameId ?? undefined,
          borderMode: nextBorder,
          borderMm: nextBorderMm,
          borderDecided: nextDecided,
          rotation: nextRotation,
          bw: nextBw,
          bwStyle: nextBwStyle,
          bwStrength: nextBwStrength,
          qty: dupSafe ? dupSafe.qty + qty : qty,
          // "Print anyway" consent was given at the OLD size — crossing to a
          // new size (incl. cross-family frame upsells) must re-ask, mirroring
          // store.convertToFramed. Keyed off target.sizeId so a merge dup's
          // own consent (already at the new size) is preserved.
          ...(sizeId !== target.sizeId ? { overrideLowRes: false } : {}),
        },
      );
      updatePrint(target.id, patch);
      if (dupSafe) removePrint(print.id);
    }

    function swapToMax(printId: string): void {
      const { prints } = state();
      const print = prints.find((p) => p.id === printId);
      if (!print) return;
      const target = swapTargetFor(print);
      if (!target) return;
      retarget(print, target.sizeId, { fitMode: print.fitMode, cropRect: null });
    }

    function removeSize(printId: string): void {
      state().removePrint(printId);
    }

    function printAnyway(printId: string): void {
      state().updatePrint(printId, { overrideLowRes: true });
    }

    function saveCropEdit(
      printId: string,
      edit: {
        sizeId: string;
        fitMode: FitMode;
        cropRect: CropRect | null;
        qty?: number;
        paper?: PaperId;
        borderMode?: BorderMode;
        borderMm?: number | null;
        frameId?: string | null;
        rotation?: Rotation;
        bw?: boolean;
        bwStyle?: BwStyle | null;
        bwStrength?: number | null;
      },
    ): void {
      const { prints } = state();
      const print = prints.find((p) => p.id === printId);
      if (!print) return;
      const opts: {
        qty?: number;
        paper?: PaperId;
        borderMode?: BorderMode;
        borderMm?: number | null;
        frameId?: string | null;
        rotation?: Rotation;
        bw?: boolean;
        bwStyle?: BwStyle | null;
        bwStrength?: number | null;
      } = {};
      if (edit.qty != null) opts.qty = edit.qty;
      if (edit.paper != null) opts.paper = edit.paper;
      if (edit.borderMode != null) opts.borderMode = edit.borderMode;
      if (edit.borderMm !== undefined) opts.borderMm = edit.borderMm;
      if (edit.frameId !== undefined) opts.frameId = edit.frameId;
      if (edit.rotation != null) opts.rotation = edit.rotation;
      if (edit.bw != null) opts.bw = edit.bw;
      if (edit.bwStyle !== undefined) opts.bwStyle = edit.bwStyle;
      if (edit.bwStrength !== undefined) opts.bwStrength = edit.bwStrength;
      retarget(print, edit.sizeId, { fitMode: edit.fitMode, cropRect: edit.cropRect }, opts);
    }

    function addPrintFromEditor(
      photoId: string,
      edit: {
        sizeId: string;
        fitMode: FitMode;
        cropRect: CropRect | null;
        qty: number;
        paper: PaperId;
        borderMode?: BorderMode;
        borderMm?: number | null;
        frameId?: string | null;
        rotation?: Rotation;
        bw?: boolean;
        bwStyle?: BwStyle | null;
        bwStrength?: number | null;
      },
    ): string | null {
      const photo = photoById(photoId);
      const entry = catalog.get(edit.sizeId);
      if (!photo || !entry || photo.status !== 'ready') return null;
      const paper = paperForSize(pricing, edit.sizeId, edit.paper);
      const frameId = edit.frameId ?? null;
      const rotation: Rotation = edit.rotation ?? 0;
      const bw = edit.bw ?? false;
      const bwStyle: BwStyle | null = bw ? edit.bwStyle ?? null : null;
      const bwStrength: number | null =
        bwStyle === 'v2' || bwStyle === 'v3' ? edit.bwStrength ?? null : null;
      const { prints, addPrints, updatePrint } = state();

      if (frameId) {
        // FRAMED add: coupled framed price, border 'none', merge on
        // photo×size×paper×frame×B&W.
        let price: number;
        try {
          price = framedProduct(pricing, frameId).price;
        } catch {
          return null;
        }
        const existing = prints.find(
          (p) =>
            p.photoId === photoId &&
            p.frameId === frameId &&
            p.sizeId === edit.sizeId &&
            p.paper === paper &&
            (p.bw ?? false) === bw &&
            (p.bwStyle ?? null) === bwStyle &&
            (p.bwStrength ?? null) === bwStrength,
        );
        if (existing) {
          const patch = recheck(existing, {
            fitMode: edit.fitMode,
            cropRect: edit.fitMode === 'crop' ? edit.cropRect : null,
            rotation,
            qty: existing.qty + edit.qty,
          });
          updatePrint(existing.id, patch);
          return existing.id;
        }
        const fields = buildPrintFields(photo, edit.sizeId, paper, edit.qty, 'none', entry, pricing, thresholds);
        const fresh: UiPrint = {
          id: uid('pr'),
          ...fields,
          unitPrice: price,
          frameId,
          borderMode: 'none',
          borderDecided: true,
          bw,
          bwStyle,
          bwStrength,
        };
        const patch = recheck(fresh, {
          fitMode: edit.fitMode,
          cropRect: edit.fitMode === 'crop' ? edit.cropRect : null,
          rotation,
        });
        addPrints([{ ...fresh, ...patch }]);
        return fresh.id;
      }

      const border = edit.borderMode ?? null;
      const nextBorder = effectiveBorderFor(edit.sizeId, border);
      const editBorderMm = nextBorder === 'white' ? edit.borderMm ?? null : null;
      const existing0 = findLooseMergeTarget(prints, photoId, edit.sizeId, paper, nextBorder, bw);
      // A differing custom border width or B&W style is a DIFFERENT print.
      const existing = existing0
        && (existing0.borderMm ?? null) === editBorderMm
        && (existing0.bwStyle ?? null) === bwStyle
        && (existing0.bwStrength ?? null) === bwStrength
          ? existing0
          : undefined;
      if (existing) {
        // Same photo×size×paper×border×B&W already in the order: sum quantities
        // and adopt the editor's crop/fit/rotation (mirrors retarget's merge).
        const patch = recheck(existing, {
          fitMode: edit.fitMode,
          cropRect: edit.fitMode === 'crop' ? edit.cropRect : null,
          rotation,
          qty: existing.qty + edit.qty,
          ...(edit.borderMode != null ? { borderMode: nextBorder, borderDecided: true } : {}),
        });
        updatePrint(existing.id, patch);
        return existing.id;
      }
      const fields = buildPrintFields(photo, edit.sizeId, paper, edit.qty, border, entry, pricing, thresholds);
      const fresh: UiPrint = { id: uid('pr'), ...fields, bw, bwStyle, bwStrength, borderMm: editBorderMm };
      const patch = recheck(fresh, {
        fitMode: edit.fitMode,
        cropRect: edit.fitMode === 'crop' ? edit.cropRect : null,
        rotation,
      });
      addPrints([{ ...fresh, ...patch }]);
      return fresh.id;
    }

    return {
      catalog,
      thresholds,
      addToSelection,
      addFramedToSelection,
      bulkApplyAll,
      ensurePrint,
      setBorder,
      bulkSetBorder,
      decideFineArtBorders,
      setQty,
      setFit,
      setCrop,
      setCustomCrop,
      bulkRatio,
      swapToMax,
      removeSize,
      printAnyway,
      saveCropEdit,
      addPrintFromEditor,
      swapTargetFor,
    };
  }, [pricing, customSizes]);
}
